import { Grid, Textarea, Box, Text, Panel, Checkbox, Button, ProgressCircle, Message, Table, Link, Tooltip, Datepicker } from '@bigcommerce/big-design';
import { InfoIcon } from '@bigcommerce/big-design-icons';
import { useState, useEffect, useCallback, useRef } from 'react';
import { useOrderExport } from '../../utils/api';
import './csvExportPage.css';

export function CSVExportsPage() {
	const [orderNumbers, setOrderNumbers] = useState('');
	const [loading, setLoading] = useState(false);
	const [error, setError] = useState(null);
	const [estimatedTime, setEstimatedTime] = useState(0);
	const [countdown, setCountdown] = useState(0);
	const [jobs, setJobs] = useState([]);
	const [jobsLoading, setJobsLoading] = useState(true);
	const [polling, setPolling] = useState(false);
	const isHoveringJobs = useRef(false);
	const [useDateRange, setUseDateRange] = useState(false);
	const [startDate, setStartDate] = useState(null);
	const [endDate, setEndDate] = useState(null);

	useEffect(() => {
		let timer;
		if (loading && countdown > 0) {
			timer = setInterval(() => {
				setCountdown(prevCountdown => (prevCountdown > 0 ? prevCountdown - 1 : 0));
			}, 1000);
		}
		return () => clearInterval(timer);
	}, [loading]);

	const [columns, setColumns] = useState({
		orderDate: false,
		customerName: true,
		customerIpAddress: false,
		customerEmail: false,
		customerPhoneNumber: false,
		orderStatus: false,
		orderTotal: false,
		currencyCode: false,
		country: true,
		tags: false,
		subTags: false,
		shippingMethod: false,
		estimatedShippingDate: false,
		shippingDate: false,
		shippingCost: false,
		productName: false,
		vendor: false,
		color: false,
		colorCode: false,
		year: false,
		make: false,
		model: false,
		partNumber: false,
		row: false,
		seat: false,
		diagram: false,
		diagramImage: false,
		price: false,
		baseCost: false,
		defaultPrice: false,
		shippingAddress: false,
		usLeadTime: false,
		canLeadTime: false,
		availabilityDescription: false,
		couponCode: false,
		couponDiscount: false
	});
	
	const { startExport, checkExportStatus, getRecentJobs, getOrderCount } = useOrderExport();

	const fetchJobs = useCallback(async () => {
		try {
			const recentJobs = await getRecentJobs();
			setJobs(recentJobs);
		} catch (err) {
			setError('Failed to load recent jobs.');
			console.error(err);
		} finally {
			setJobsLoading(false);
		}
	}, [getRecentJobs]);

	useEffect(() => {
		fetchJobs(); // Initial fetch
		const intervalId = setInterval(() => {
            if (!isHoveringJobs.current) {
                fetchJobs();
            }
        }, 3000);

		return () => clearInterval(intervalId); // Cleanup on unmount
	}, [fetchJobs]);

	const estimateTime = useCallback((orderCount, currentColumns) => {
		if (orderCount === 0) {
			setEstimatedTime(0);
			return;
		}

		const selectedColumns = Object.keys(currentColumns).filter(key => currentColumns[key]);
		if (selectedColumns.length === 0) {
			setEstimatedTime(0);
			return;
		}

		const calls = new Set();

		if (selectedColumns.includes('customerIpAddress') || selectedColumns.includes('estimatedShippingDate')) {
			calls.add('orderDetails');
		}
		if (
			selectedColumns.includes('baseCost') || 
			selectedColumns.includes('defaultPrice') || 
			selectedColumns.includes('usLeadTime') || 
			selectedColumns.includes('canLeadTime') ||
			selectedColumns.includes('availabilityDescription') ||
			selectedColumns.includes('estimatedShippingDate') ||
			selectedColumns.includes('colorCode')
		) {
			calls.add('orderProducts');
		}
		if (selectedColumns.includes('customerPhoneNumber') || selectedColumns.includes('shippingCost')) {
			calls.add('shippingAddresses');
		}
		if (selectedColumns.includes('couponCode') || selectedColumns.includes('couponDiscount')) {
			calls.add('coupons');
		}

		const apiCallsPerOrder = calls.size;
		let totalApiCalls = orderCount * apiCallsPerOrder;

		const needsCatalogCall = 
			selectedColumns.includes('defaultPrice') || 
			selectedColumns.includes('usLeadTime') || 
			selectedColumns.includes('canLeadTime') ||
			selectedColumns.includes('availabilityDescription') ||
			selectedColumns.includes('estimatedShippingDate');

		if (needsCatalogCall) {
			// Assume roughly one product per order for estimation purposes.
			// The backend fetches unique products in batches of 50.
			const catalogApiCalls = Math.ceil(orderCount / 50);
			totalApiCalls += catalogApiCalls;
		}

		let timeInSeconds = Math.ceil(totalApiCalls * 0.5); 

		if (timeInSeconds > 0) {
			timeInSeconds += 30;
		}

		setEstimatedTime(timeInSeconds);
	}, []);

	useEffect(() => {
		const calculateEstimate = async () => {
			if (useDateRange && startDate && endDate) {
				try {
					const { orderCount } = await getOrderCount(new Date(startDate).toISOString(), new Date(endDate).toISOString());
					estimateTime(orderCount, columns);
				} catch (err) {
					setError('Failed to get order count for date range.');
					console.error(err);
				}
			} else if (!useDateRange) {
				const orderIds = orderNumbers.split(/[\s,]+/).map(id => id.trim()).filter(id => id);
				estimateTime(orderIds.length, columns);
			} else {
				setEstimatedTime(0);
			}
		};
		calculateEstimate();
	}, [useDateRange, startDate, endDate, orderNumbers, columns, getOrderCount, estimateTime]);

	const handleOrderNumbersChange = (event) => {
		setOrderNumbers(event.target.value);
	};

	const handleColumnChange = (column) => {
		const newColumns = {
			...columns,
			[column]: !columns[column]
		};
		setColumns(newColumns);
	};

	const handleSelectAll = () => {
		const allSelected = Object.values(columns).every(value => value);
		const newState = {};
		
		Object.keys(columns).forEach(key => {
			newState[key] = !allSelected;
		});
		
		setColumns(newState);
	};

	const areAllSelected = Object.values(columns).every(value => value);

	const handleExport = useCallback(async () => {
		setLoading(true);
		setError(null);
		setCountdown(estimatedTime);
		
		try {
			// Parse order numbers from text area
			const orderIds = orderNumbers
				.split(/[\s,]+/) // Split by commas or whitespace
				.map(id => id.trim())
				.filter(id => id); // Remove empty strings

			if (orderIds.length === 0 && !useDateRange) {
				throw new Error('Please enter at least one order number or select a date range.');
			}
			
			if (useDateRange && (!startDate || !endDate)) {
				throw new Error('Please select a start and end date for the date range export.');
			}
			
			const selectedColumns = Object.keys(columns).filter(key => columns[key]);
			
			if (selectedColumns.length === 0) {
				throw new Error('Please select at least one column to export');
			}
			
			const exportPayload = {
				orderIds: useDateRange ? [] : orderIds,
				columns: selectedColumns,
				startDate: useDateRange ? new Date(startDate).toISOString() : null,
				endDate: useDateRange ? new Date(endDate).toISOString() : null,
			};
			
			const { jobId } = await startExport(exportPayload);
			setPolling(true);

			const poll = async () => {
				const result = await checkExportStatus(jobId);

				switch (result.status) {
					case 'completed':
						setLoading(false);
						setPolling(false);
						// Create and download CSV file
						Object.assign(document.createElement('a'), {
							target: '_blank',
							href: result.file_url,
							download: `orders-export-${jobId}.csv`,
						}).click();
						break;
					case 'failed':
						setLoading(false);
						setPolling(false);
						setError(result.error_message || 'Export failed');
						break;
					default:
						// If still pending or processing, poll again after 3 seconds
						setTimeout(poll, 3000);
						break;
				}
			};

			poll();

		} catch (err) {
			console.error('CSV export error:', err);
			setError(err.message || 'Failed to export orders');
			setLoading(false);
			setCountdown(0);
		}
	}, [
		estimatedTime,
		orderNumbers,
		useDateRange,
		startDate,
		endDate,
		columns,
		startExport,
		checkExportStatus,
	]);

	const formatTime = (seconds) => {
		const h = Math.floor(seconds / 3600).toString().padStart(2, '0');
		const m = Math.floor((seconds % 3600) / 60).toString().padStart(2, '0');
		const s = (seconds % 60).toString().padStart(2, '0');
		return `${h}:${m}:${s}`;
	};

	const jobsColumns = [
		{ header: 'Id', hash: 'id', render: ({ id }) => (
			<Box display="flex" alignItems="center" gridGap="small">
				<Text>{id}</Text>
			</Box>
		) },
		{ header: 'Created At', hash: 'created_at', render: ({ created_at }) => <Text>{new Date(created_at).toLocaleString()}</Text> },
		{
			header: 'Status',
			hash: 'status',
			render: ({ status }) => {
				let color;
				switch (status?.toLowerCase()) {
					case 'completed':
						color = 'success';
						break;
					case 'failed':
						color = 'danger';
						break;
					case 'pending':
						color = 'secondary';
						break;
					case 'processing':
						color = 'primary';
						break;
					default:
						color = 'secondary';
				}
				return <Text color={color} bold>{status}</Text>;
			}
		},
		{ header: 'User', hash: 'user', render: ({ user }) => <Text>{user}</Text> },
		{ header: 'File URL', hash: 'file_url', render: ({ file_url }) => 
			file_url ? <Link href={file_url} target="_blank">Download</Link> : <Text>N/A</Text> 
		},
	];

	return (
		<Box style={{ width: '100%', margin: '0 auto' }}>
			{error && (
				<Box marginBottom="medium">
					<Message type="error">{error}</Message>
				</Box>
			)}
			
			<Grid gridColumns="1fr 1fr 3fr">
				{/* Left Column - Columns Selection */}
				<Panel header="Columns" marginRight="5px">
					<Box marginBottom="medium">
						<Text marginBottom="small">Select columns to include in CSV:</Text>
						<Grid gridGap="small">
							<Checkbox 
								label="Select All"
								checked={areAllSelected}
								onChange={handleSelectAll}
								marginBottom="small"
							/>
							<br/>
							<Checkbox 
								label="Order date" 
								checked={columns.orderDate}
								onChange={() => handleColumnChange('orderDate')}
							/>

							<Checkbox 
								label="Customer name" 
								checked={columns.customerName}
								onChange={() => handleColumnChange('customerName')}
							/>
							<Checkbox 
								label="Customer email" 
								checked={columns.customerEmail}
								onChange={() => handleColumnChange('customerEmail')}
							/>
							<Checkbox 
								label="Customer phone number" 
								checked={columns.customerPhoneNumber}
								onChange={() => handleColumnChange('customerPhoneNumber')}
							/>
							<Checkbox 
								label="Country" 
								checked={columns.country}
								onChange={() => handleColumnChange('country')}
							/>
							<Checkbox 
								label="Tags" 
								checked={columns.tags}
								onChange={() => handleColumnChange('tags')}
							/>
							<Checkbox 
								label="Child Tags" 
								checked={columns.subTags}
								onChange={() => handleColumnChange('subTags')}
							/>
							<Checkbox 
								label="Currency code" 
								checked={columns.currencyCode}
								onChange={() => handleColumnChange('currencyCode')}
							/>
							<Checkbox 
								label="Order status" 
								checked={columns.orderStatus}
								onChange={() => handleColumnChange('orderStatus')}
							/>
							<Checkbox 
								label="Order total" 
								checked={columns.orderTotal}
								onChange={() => handleColumnChange('orderTotal')}
							/>
							<Checkbox 
								label="Customer IP address" 
								checked={columns.customerIpAddress}
								onChange={() => handleColumnChange('customerIpAddress')}
							/>
							<Checkbox 
								label="Shipping address" 
								checked={columns.shippingAddress}
								onChange={() => handleColumnChange('shippingAddress')}
							/>
							<Checkbox 
								label="Shipping method" 
								checked={columns.shippingMethod}
								onChange={() => handleColumnChange('shippingMethod')}
							/>
							<Checkbox 
								label="Estimated shipping date" 
								checked={columns.estimatedShippingDate}
								onChange={() => handleColumnChange('estimatedShippingDate')}
							/>
							<Checkbox 
								label="Shipping date" 
								checked={columns.shippingDate}
								onChange={() => handleColumnChange('shippingDate')}
							/>
							<Checkbox 
								label="Shipping cost" 
								checked={columns.shippingCost}
								onChange={() => handleColumnChange('shippingCost')}
							/>
							<Checkbox 
								label="Product name" 
								checked={columns.productName}
								onChange={() => handleColumnChange('productName')}
							/>
							<Checkbox 
								label="Vendor" 
								checked={columns.vendor}
								onChange={() => handleColumnChange('vendor')}
							/>
							<Checkbox 
								label="Color" 
								checked={columns.color}
								onChange={() => handleColumnChange('color')}
							/>
							<Checkbox 
								label="Color Code" 
								checked={columns.colorCode}
								onChange={() => handleColumnChange('colorCode')}
							/>
							<Checkbox 
								label="Year" 
								checked={columns.year}
								onChange={() => handleColumnChange('year')}
							/>
							<Checkbox 
								label="Make" 
								checked={columns.make}
								onChange={() => handleColumnChange('make')}
							/>
							<Checkbox 
								label="Model" 
								checked={columns.model}
								onChange={() => handleColumnChange('model')}
							/>
							<Checkbox 
								label="Part number" 
								checked={columns.partNumber}
								onChange={() => handleColumnChange('partNumber')}
							/>
							<Checkbox 
								label="Row" 
								checked={columns.row}
								onChange={() => handleColumnChange('row')}
							/>
							<Checkbox 
								label="Seat" 
								checked={columns.seat}
								onChange={() => handleColumnChange('seat')}
							/>
							<Checkbox 
								label="Diagram" 
								checked={columns.diagram}
								onChange={() => handleColumnChange('diagram')}
							/>
							<Checkbox 
								label="Diagram Image" 
								checked={columns.diagramImage}
								onChange={() => handleColumnChange('diagramImage')}
							/>
							<Checkbox 
								label="Sale Price" 
								checked={columns.price}
								onChange={() => handleColumnChange('price')}
							/>
							<Checkbox 
								label="Cost" 
								checked={columns.baseCost}
								onChange={() => handleColumnChange('baseCost')}
							/>
							<Checkbox 
								label="Default Price" 
								checked={columns.defaultPrice}
								onChange={() => handleColumnChange('defaultPrice')}
							/>
							<Checkbox 
								label="US Lead Time" 
								checked={columns.usLeadTime}
								onChange={() => handleColumnChange('usLeadTime')}
							/>
							<Checkbox 
								label="CAN Lead Time" 
								checked={columns.canLeadTime}
								onChange={() => handleColumnChange('canLeadTime')}
							/>
							<Checkbox 
								label="Availability Description" 
								checked={columns.availabilityDescription}
								onChange={() => handleColumnChange('availabilityDescription')}
							/>
							<Checkbox 
								label="Coupon Code" 
								checked={columns.couponCode}
								onChange={() => handleColumnChange('couponCode')}
							/>
							<Checkbox 
								label="Coupon Discount" 
								checked={columns.couponDiscount}
								onChange={() => handleColumnChange('couponDiscount')}
							/>
						</Grid>
					</Box>
				</Panel>

				{/* Middle Column - Order Numbers and Export Button */}
				<Panel header="Orders" marginRight="5px">
					<Box marginBottom="small">
						<Checkbox 
							label="Export by Date Range"
							checked={useDateRange}
							onChange={() => setUseDateRange(!useDateRange)}
						/>
					</Box>

					{useDateRange && (
						<Grid gridColumns="1fr 1fr" gridGap="medium">
							<Datepicker
								label="Start Date"
								value={startDate}
								onDateChange={setStartDate}
								maxDate={endDate ? new Date(endDate) : new Date()}
							/>
							<Datepicker
								label="End Date"
								value={endDate}
								onDateChange={setEndDate}
								minDate={startDate ? new Date(startDate) : undefined}
								maxDate={new Date()}
							/>
						</Grid>
					)}

					<Box marginBottom="medium" marginTop="medium">
						<Box className="textarea-container" style={{ height: '700px' }}>
							<Textarea 
								value={orderNumbers}
								onChange={handleOrderNumbersChange}
								placeholder="Enter order numbers, separated by commas or new lines"
								rows={50}
								fullWidth
								disabled={useDateRange}
							/>
						</Box>
					</Box>
					<Box marginTop="medium">
						{loading ? (
							<Box marginTop="medium">
								<div className="progress-container">
									<ProgressCircle size="small" />
									<div className="progress-text">
										<Text>Exporting...</Text>
										<Text bold>Estimated time remaining: {formatTime(countdown)}</Text>
									</div>
								</div>
								<div className="progress-bar-container">
									<div 
										className="progress-bar"
										style={{ animation: `countdown ${estimatedTime}s linear`}}
									></div>
								</div>
							</Box>
						) : (
							<Box marginTop="medium">
								{estimatedTime > 0 && (
									<Text>Estimated time: {formatTime(estimatedTime)}</Text>
								)}
								<Button 
									variant="primary"
									onClick={handleExport}
									disabled={loading || (orderNumbers.trim() === '' && !useDateRange)}
								>
									{loading ? <ProgressCircle size="xSmall" /> : 'Export to CSV'}
								</Button>
							</Box>
						)}
					</Box>
				</Panel>

				{/* Right Column - Recent Exports */}
				<Panel
					header="Recent Exports"
					onMouseEnter={() => (isHoveringJobs.current = true)}
					onMouseLeave={() => (isHoveringJobs.current = false)}
				>
					<Table
						columns={jobsColumns}
						items={jobs}
						isLoading={jobsLoading && !jobs.length}
						keyField="id"
					/>
				</Panel>
			</Grid>
		</Box>
	);
}
